import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check, Sparkles, Building2, ShieldCheck, ArrowRight, Zap,
  TrendingUp, Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { keyed } from '../lib/listKeys';
import { LOGO_MARK } from '../lib/brand';
import api from '../lib/api';

const CACHE_KEY = 'dutilyne_public_plans_cache';

// Shown only if the API has never loaded successfully on this device —
// the real content always comes from /public/plans, managed in the
// platform console's Plans screen.
const FALLBACK_PLANS = [
  { plan_id: 'essentials', name: 'Essentials', price: 25, billing_period: 'month',
    description: 'For independent pharmacies just getting started', cta_text: 'Start free 14-day trial',
    is_highlighted: false, annual_discount_pct: 20, features: ['Smart POS · Inventory · Patient register', '16 reports · CSV/Excel/PDF export'] },
  { plan_id: 'professional', name: 'Professional', price: 89, billing_period: 'month',
    description: 'For growing chains and clinic-attached pharmacies', cta_text: 'Start free 14-day trial',
    is_highlighted: true, annual_discount_pct: 20, features: ['Everything in Essentials, plus:', 'Multi-branch · Suppliers · HR & Payroll'] },
  { plan_id: 'enterprise', name: 'Enterprise', price: 249, billing_period: 'month',
    description: 'For chains, hospital pharmacies & compliance-driven ops', cta_text: 'Talk to sales',
    is_highlighted: false, annual_discount_pct: 20, features: ['Everything in Professional, plus:', 'PHI encryption · HIPAA audit log · SLA'] },
];

const ICONS = [Zap, TrendingUp, Building2];
const COLORS = ['emerald', 'sky', 'amber'];

const ADDONS = [
  { name: 'Extra branch', price: 'GHS 200/mo', tier: 'Professional' },
  { name: 'Extra 5 users', price: 'GHS 150/mo', tier: 'Professional' },
  { name: 'Extra 2,000 AI calls', price: 'GHS 250/mo', tier: 'Any' },
  { name: 'Client Portal PWA', price: 'GHS 600/mo', tier: 'Enterprise' },
  { name: 'Mobile staff app', price: 'GHS 800/mo', tier: 'Enterprise' },
  { name: 'Customer Loyalty Programme', price: 'GHS 400/mo', tier: 'Any' },
  { name: 'Custom integration / migration', price: 'Custom quote', tier: 'Enterprise' },
];

const FAQS = [
  {
    q: 'Can I switch plans later?',
    a: 'Yes — upgrades are instant; downgrades apply at the next billing cycle. Your data, branches, and audit trail are preserved across plan changes.',
  },
  {
    q: 'What happens if my monthly AI calls are used?',
    a: 'Admins see upgrade nudges at 80% and 95%. At the monthly cap, new AI requests pause until the next UTC month or a plan upgrade; existing ERP workflows continue normally.',
  },
  {
    q: 'Do I need internet to run the POS?',
    a: 'Essentials and Professional are cloud-first with offline IndexedDB sync for the POS. Enterprise ships a real Windows desktop installer with bundled MongoDB — runs entirely offline on your LAN.',
  },
  {
    q: 'Is my patient data secure under HIPAA / GDPR?',
    a: 'Enterprise tier includes AES-256-GCM PHI encryption at rest, per-record access audit logs (HIPAA §164.528), brute-force lockout, sliding session timeouts, and PHI redaction in server logs. We can sign a BAA on request.',
  },
  {
    q: 'Can I bring my own AI keys?',
    a: 'Yes — Enterprise customers can plug in their own OpenAI / Gemini / Claude keys (BYOK) and keep all AI usage on their billing account. Universal Key remains available as fallback.',
  },
  {
    q: 'Annual discount?',
    a: 'Pay annually and get 20% off any tier — equivalent to ~2.4 months free.',
  },
];

const loadCachedPlans = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export default function PlansPage() {
  const [tiers, setTiers] = useState(() => loadCachedPlans() || FALLBACK_PLANS);
  const [annual, setAnnual] = useState(false);
  const [currency, setCurrency] = useState('usd');
  const [ghsRate, setGhsRate] = useState(12);

  useEffect(() => {
    api.get('/public/plans').then((res) => {
      const live = res.data?.plans;
      if (Array.isArray(live) && live.length > 0) {
        setTiers(live);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(live)); } catch { /* storage unavailable */ }
      }
      if (res.data?.usd_ghs_rate) setGhsRate(res.data.usd_ghs_rate);
    }).catch(() => { /* keep showing cached/fallback plans */ });
  }, []);

  /** Format a USD amount in the selected currency */
  const fmt = (usd) => {
    const val = currency === 'ghs' ? usd * ghsRate : usd;
    if (currency === 'ghs') {
      return val === Math.round(val)
        ? `GHS ${Math.round(val).toLocaleString()}`
        : `GHS ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$${val.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-[#0b1715] text-slate-100" data-testid="plans-page">
      {/* ── Top bar ── */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0b1715]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="plans-logo-link">
            <img src={LOGO_MARK} alt="" aria-hidden="true"
              className="h-9 w-9 rounded-lg object-cover border border-emerald-500/20" />
            <span className="leading-none">
              <span className="block font-bold text-lg tracking-tight">DutiLyne</span>
              <span className="block text-[9px] uppercase tracking-[0.18em] text-slate-400">Pharmacy POS</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="plans-login-link">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-white/5">Sign in</Button>
            </Link>
            <Link to="/signup/pharmacy" data-testid="plans-trial-link">
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                Start free trial
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="max-w-5xl mx-auto px-6 pt-20 pb-12 text-center">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/5 px-3 py-1 text-xs text-emerald-300 mb-6">
          <Sparkles className="h-3 w-3" />
          AI-powered pharmacy ERP · trusted across Ghana, Nigeria & beyond
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
          Pricing that scales <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-sky-300">
            with your pharmacy
          </span>
        </h1>
        <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto">
          From a single counter to a hospital chain — pick the tier that fits today
          and trade up as you grow. Every plan includes our clinical-grade safety net.
        </p>
      </section>

      {/* ── Billing + Currency toggles ── */}
      <section className="max-w-5xl mx-auto px-6 pb-10">
        <div className="flex items-center justify-center gap-3 flex-wrap" data-testid="billing-toggle">
          <span className={`text-sm font-semibold transition-colors ${!annual ? 'text-white' : 'text-slate-500'}`}>Monthly</span>
          <button
            type="button"
            onClick={() => setAnnual((v) => !v)}
            role="switch"
            aria-checked={annual}
            className={`relative w-14 h-7 rounded-full transition-colors ${annual ? 'bg-emerald-500' : 'bg-slate-600'}`}
            data-testid="billing-toggle-switch"
            aria-label={annual ? 'Switch to monthly billing' : 'Switch to annual billing'}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${annual ? 'translate-x-7' : ''}`} />
          </button>
          <span className={`text-sm font-semibold transition-colors ${annual ? 'text-white' : 'text-slate-500'}`}>Annual</span>
          <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-0.5">
            Save up to 20%
          </span>

          <span className="w-px h-5 bg-white/10 mx-1.5 hidden sm:block" aria-hidden="true" />

          {/* Currency pill toggle */}
          <div className="inline-flex rounded-full border border-white/10 p-0.5" data-testid="currency-toggle">
            {['usd', 'ghs'].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCurrency(c)}
                className={`px-3.5 py-1 text-xs font-bold uppercase tracking-wide rounded-full transition-colors ${
                  currency === c
                    ? 'bg-white/10 text-white'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
                data-testid={`currency-toggle-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tier cards ── */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid lg:grid-cols-3 gap-5">
          {tiers.map((t, idx) => {
            const Icon = ICONS[idx % ICONS.length];
            const color = COLORS[idx % COLORS.length];
            const discountPct = t.annual_discount_pct ?? 20;
            const monthlyPrice = Number(t.price);
            const annualTotal = Math.round(monthlyPrice * 12 * (1 - discountPct / 100));
            const annualMonthly = Math.round(annualTotal / 12);
            const displayPrice = annual ? annualMonthly : monthlyPrice;
            const accent = {
              emerald: 'border-emerald-500/20 hover:border-emerald-500/40',
              sky: 'border-sky-500/30 hover:border-sky-500/50',
              amber: 'border-amber-500/20 hover:border-amber-500/40',
            }[color];
            const iconBg = {
              emerald: 'bg-emerald-500/10 text-emerald-400',
              sky: 'bg-sky-500/10 text-sky-400',
              amber: 'bg-amber-500/10 text-amber-400',
            }[color];
            const bulletColor = {
              emerald: 'text-emerald-400',
              sky: 'text-sky-400',
              amber: 'text-amber-400',
            }[color];
            const ctaCls = t.is_highlighted
              ? 'bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold'
              : 'bg-white/5 hover:bg-white/10 border border-white/10 text-white';
            /* Resolve CTA destination: custom signup_url > mailto detection > default signup page */
            const ctaTarget = (() => {
              if (t.signup_url) return t.signup_url;
              if ((t.cta_text || '').toLowerCase().includes('sales')) return 'mailto:sales@dutilyne.com';
              const cycle = annual ? 'annual' : 'monthly';
              const planKey = (t.name || '').toLowerCase().replace(/\s+/g, '-');
              const cur = currency !== 'usd' ? `&currency=${currency}` : '';
              return `/signup/pharmacy?plan=${planKey}&cycle=${cycle}${cur}`;
            })();

            return (
              <div
                key={t.plan_id}
                data-testid={`plan-${t.plan_id}`}
                className={`relative rounded-2xl border ${accent} bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-7 transition-all hover:-translate-y-1 ${
                  t.is_highlighted ? 'lg:-mt-4 lg:mb-4 ring-1 ring-emerald-500/30 shadow-2xl shadow-emerald-500/10' : ''
                }`}
              >
                {t.is_highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-orange-400 text-orange-950 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full inline-flex items-center gap-1">
                      <Star className="h-3 w-3 fill-current" />
                      Most popular
                    </span>
                  </div>
                )}

                <div className={`h-10 w-10 rounded-xl ${iconBg} flex items-center justify-center mb-5`}>
                  <Icon className="h-5 w-5" />
                </div>

                <h3 className="text-xl font-bold tracking-tight">{t.name}</h3>
                <p className="text-sm text-slate-400 mt-1 mb-5 min-h-[40px]">{t.description}</p>

                <div className="mb-5">
                  <span className="text-4xl font-bold tabular-nums" data-testid={`plan-${t.plan_id}-price`}>
                    {fmt(displayPrice)}
                  </span>
                  <span className="text-slate-400 text-sm ml-1.5">/ {annual ? 'mo, billed annually' : t.billing_period}</span>
                  {annual && discountPct > 0 && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="text-xs text-slate-500 line-through">{fmt(monthlyPrice)}/mo</span>
                      <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2 py-0.5">
                        {discountPct}% off
                      </span>
                    </div>
                  )}
                </div>

                {ctaTarget.startsWith('mailto:') || ctaTarget.startsWith('http') ? (
                  <a href={ctaTarget} {...(ctaTarget.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})} data-testid={`plan-${t.plan_id}-cta-link`}>
                    <Button className={`w-full ${ctaCls} h-10`} data-testid={`plan-${t.plan_id}-cta`}>
                      {t.cta_text}
                      <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                  </a>
                ) : (
                  <Link to={ctaTarget} data-testid={`plan-${t.plan_id}-cta-link`}>
                    <Button className={`w-full ${ctaCls} h-10`} data-testid={`plan-${t.plan_id}-cta`}>
                      {t.cta_text}
                      <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                  </Link>
                )}

                {/* Features */}
                <ul className="space-y-2.5 mt-5">
                  {keyed(t.features || [], (f) => f).map(({ item: f, key }) => (
                    <li key={key} className="flex items-start gap-2.5 text-sm">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${bulletColor}`} />
                      <span className="text-slate-300">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Add-ons strip ── */}
      <section className="max-w-7xl mx-auto px-6 py-12 border-t border-white/5">
        <div className="grid md:grid-cols-3 gap-4 items-start">
          <div className="md:col-span-1">
            <h2 className="text-2xl font-bold tracking-tight mb-2">Add-ons</h2>
            <p className="text-sm text-slate-400">
              Bolt on extra capacity or premium modules without changing your tier.
            </p>
          </div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ADDONS.map((a) => (
              <div
                key={a.name}
                data-testid={`addon-${a.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.03] px-4 py-3 hover:border-white/10 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium">{a.name}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">{a.tier}</p>
                </div>
                <p className="text-sm font-semibold tabular-nums text-emerald-300">{a.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Compliance band ── */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="rounded-2xl border border-white/5 bg-gradient-to-r from-emerald-500/5 via-sky-500/5 to-amber-500/5 p-8 md:p-10">
          <div className="grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-1">
              <ShieldCheck className="h-8 w-8 text-emerald-400 mb-3" />
              <h2 className="text-2xl font-bold tracking-tight mb-2">Built for the auditors</h2>
              <p className="text-sm text-slate-400">
                Compliance isn't an afterthought — it's the spine of the Enterprise tier.
              </p>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { t: 'PHI Encryption at Rest', d: 'AES-256-GCM on every patient field' },
                { t: 'HIPAA §164.528 Audit Log', d: 'Per-record access trail, exportable to CSV' },
                { t: 'Auto-Logout', d: 'Sliding 15-min session timeout' },
                { t: 'Brute-Force Lockout', d: '5 attempts / 15 min cooldown' },
                { t: 'Permission Audit', d: 'Who has what · CSV-ready for SOC-2' },
                { t: 'GRA-Ready Statutory', d: 'PAYE · SSNIT · NHIL · GETFund payables' },
              ].map(item => (
                <div key={item.t} className="rounded-lg bg-white/[0.03] border border-white/5 px-4 py-3">
                  <p className="text-sm font-semibold">{item.t}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{item.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold tracking-tight text-center mb-10">Questions, answered</h2>
        <div className="space-y-3">
          {keyed(FAQS, f => f.q).map(({ item: f, key }, i) => (
            <details
              key={key}
              data-testid={`faq-${i}`}
              className="group rounded-lg border border-white/5 bg-white/[0.02] open:bg-white/[0.04] open:border-white/10 transition-colors"
            >
              <summary className="cursor-pointer list-none flex items-start justify-between gap-4 px-5 py-4 text-sm font-medium">
                <span>{f.q}</span>
                <span className="text-slate-500 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
              </summary>
              <div className="px-5 pb-4 text-sm text-slate-400 leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 to-emerald-500/0 p-10 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Ready to modernise your pharmacy?
          </h2>
          <p className="text-slate-400 mb-7 max-w-xl mx-auto">
            14-day free trial on Essentials and Professional. No credit card required.
            Hospital chains — book a demo with our team.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/signup/pharmacy" data-testid="bottom-cta-trial">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                Start free trial
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <a href="mailto:sales@dutilyne.com" data-testid="bottom-cta-sales">
              <Button size="lg" variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white">
                Talk to sales
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>© 2026 DutiLyne · AI-powered pharmacy ERP</p>
          <div className="flex items-center gap-5">
            <Link to="/login" className="hover:text-slate-300 transition-colors">Sign in</Link>
            <a href="mailto:sales@dutilyne.com" className="hover:text-slate-300 transition-colors">Contact sales</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
